import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, FileWarning, Pen, Sparkles } from "lucide-react";
import MemedWidget from "@/components/MemedWidget";

const blank = { name: "", dosage: "", frequency: "", duration: "", notes: "" };
let __medRowKey = 0;
const newBlank = () => ({ ...blank, __key: ++__medRowKey });

export default function PrescriptionDialog({ open, onOpenChange, appointment }) {
  const [meds, setMeds] = useState([newBlank()]);
  const [notes, setNotes] = useState("");
  const [cid, setCid] = useState("");
  const [memedStatus, setMemedStatus] = useState({ enabled: false, message: "" });
  const [existing, setExisting] = useState([]);
  const [busy, setBusy] = useState(false);
  const [memedOpen, setMemedOpen] = useState(false);

  useEffect(() => {
    if (!open || !appointment) return;
    api.get("/memed/status").then((r) => setMemedStatus(r.data)).catch(() => {});
    api.get(`/prescriptions?appointment_id=${appointment.id}`).then((r) => setExisting(r.data)).catch(() => {});
  }, [open, appointment]);

  const updMed = (i, k, v) => setMeds((prev) => prev.map((m, ix) => ix === i ? { ...m, [k]: v } : m));
  const addMed = () => setMeds((prev) => [...prev, newBlank()]);
  const rmMed = (i) => setMeds((prev) => prev.filter((_, ix) => ix !== i));

  const save = async () => {
    const filtered = meds.filter((m) => m.name.trim() && m.dosage.trim()).map(({ __key, ...rest }) => rest);
    if (filtered.length === 0) { toast.error("Adicione pelo menos um medicamento com nome e dose"); return; }
    setBusy(true);
    try {
      await api.post("/prescriptions", {
        appointment_id: appointment.id,
        medications: filtered,
        clinical_notes: notes || null,
        cid_codes: cid ? cid.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      toast.success("Rascunho de prescrição salvo");
      setMeds([newBlank()]); setNotes(""); setCid("");
      const r = await api.get(`/prescriptions?appointment_id=${appointment.id}`);
      setExisting(r.data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  const signMemed = async (id) => {
    try { await api.post(`/prescriptions/${id}/memed-sign`); toast.success("Assinado via Memed"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-navy-900">Prescrição — {appointment?.patient_name}</DialogTitle>
        </DialogHeader>

        {/* Memed status banner */}
        <div className={`rounded-xl p-3 text-sm flex gap-2 ${memedStatus.enabled ? "bg-teal-50 text-teal-800" : "bg-amber-50 text-amber-900 border border-amber-200"}`} data-testid="memed-status-banner">
          <FileWarning className="w-4 h-4 mt-0.5 shrink-0" />
          <div>{memedStatus.message || "Verificando status Memed…"}</div>
        </div>

        {existing.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium text-slate-700">Prescrições desta consulta</div>
            {existing.map((p) => (
              <div key={p.id} className="border border-slate-200 rounded-xl p-3 bg-slate-50" data-testid={`presc-item-${p.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 text-sm">
                    <div className="text-xs text-slate-500">{new Date(p.created_at).toLocaleString("pt-BR")} · <span className={`font-medium ${p.status === "signed" ? "text-teal-700" : "text-amber-700"}`}>{p.status === "signed" ? "Assinada (ICP-Brasil)" : "Rascunho — sem validade legal"}</span></div>
                    <ul className="mt-1 list-disc pl-5">
                      {p.medications.map((m, i) => <li key={`${p.id}-${m.name}-${i}`}><b>{m.name}</b> — {m.dosage}{m.frequency ? ` · ${m.frequency}` : ""}{m.duration ? ` · ${m.duration}` : ""}</li>)}
                    </ul>
                    {p.cid_codes?.length > 0 && <div className="text-xs text-slate-600 mt-1">CID: {p.cid_codes.join(", ")}</div>}
                  </div>
                  {p.status === "draft" && (
                    <Button size="sm" variant="outline" onClick={()=>signMemed(p.id)} disabled={!memedStatus.enabled} data-testid={`presc-sign-${p.id}`}>
                      <Pen className="w-3 h-3 mr-1" />Assinar Memed
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3">
          <div className="text-sm font-medium text-slate-700">Nova prescrição</div>
          {meds.map((m, i) => (
            <div key={m.__key} className="border border-slate-200 rounded-xl p-3 space-y-2 relative" data-testid={`med-row-${i}`}>
              <div className="grid sm:grid-cols-2 gap-2">
                <div><Label className="text-xs">Medicamento *</Label><Input value={m.name} onChange={(e)=>updMed(i, "name", e.target.value)} placeholder="Ex: Metilfenidato 10mg" data-testid={`med-name-${i}`} /></div>
                <div><Label className="text-xs">Dose *</Label><Input value={m.dosage} onChange={(e)=>updMed(i, "dosage", e.target.value)} placeholder="Ex: 1 comprimido" data-testid={`med-dosage-${i}`} /></div>
                <div><Label className="text-xs">Frequência</Label><Input value={m.frequency} onChange={(e)=>updMed(i, "frequency", e.target.value)} placeholder="Ex: 1x ao dia pela manhã" data-testid={`med-freq-${i}`} /></div>
                <div><Label className="text-xs">Duração</Label><Input value={m.duration} onChange={(e)=>updMed(i, "duration", e.target.value)} placeholder="Ex: 30 dias" data-testid={`med-dur-${i}`} /></div>
              </div>
              <Textarea rows={2} value={m.notes} onChange={(e)=>updMed(i, "notes", e.target.value)} placeholder="Observações (opcional)" data-testid={`med-notes-${i}`} />
              {meds.length > 1 && (
                <button className="absolute top-2 right-2 text-red-500 hover:text-red-700" onClick={()=>rmMed(i)} data-testid={`med-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addMed} data-testid="med-add"><Plus className="w-4 h-4 mr-1" />Adicionar medicamento</Button>

          <div><Label className="text-xs">CID-10 (separado por vírgula)</Label><Input value={cid} onChange={(e)=>setCid(e.target.value)} placeholder="Ex: F90.0, F84.0" data-testid="presc-cid" /></div>
          <div><Label className="text-xs">Observações clínicas</Label><Textarea rows={3} value={notes} onChange={(e)=>setNotes(e.target.value)} data-testid="presc-notes" /></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)}>Fechar</Button>
          <Button
            onClick={() => setMemedOpen(true)}
            disabled={!memedStatus.enabled}
            className="bg-navy-900 hover:bg-navy-800 text-white"
            data-testid="presc-open-memed"
            title={!memedStatus.enabled ? "Integração Memed ainda não ativada" : "Abrir Prescrição Digital com assinatura ICP-Brasil"}
          >
            <Sparkles className="w-4 h-4 mr-1" />Prescrição Digital (Memed)
          </Button>
          <Button onClick={save} disabled={busy} className="bg-teal-500 hover:bg-teal-600" data-testid="presc-save">
            {busy ? "Salvando..." : "Salvar rascunho"}
          </Button>
        </DialogFooter>
      </DialogContent>
      <MemedWidget
        open={memedOpen}
        onOpenChange={setMemedOpen}
        appointment={appointment}
        onSigned={async () => {
          const r = await api.get(`/prescriptions?appointment_id=${appointment.id}`);
          setExisting(r.data);
        }}
      />
    </Dialog>
  );
}
